// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { TypographyProps } from '@iheartjane/reefer';
import { Button, Flex, Link, Typography } from '@iheartjane/reefer';
import { useMemo } from 'react';

import type {
  AlgoliaProduct,
  ArrayFilterValue,
  FilterGroup,
  FilterKeys,
  JaneSearchState,
  ProductFilterKeys,
} from '@jane/search/types';

import { useKioskSpacing } from '../../../hooks';
import { isFilterSelected } from '../../../util';
import { Grid } from '../Grid/Grid';

interface FilterSelectionsProps {
  filterGroup: FilterGroup;
  onChange: (key: ProductFilterKeys, value: ArrayFilterValue) => void;
  onDeselect: (key: ProductFilterKeys) => void;
  searchState: JaneSearchState<AlgoliaProduct>;
}

const FilterButton = styled(Button)(() => {
  return { div: { maxWidth: '100%' } };
});

export const FilterSelections = ({
  filterGroup,
  onChange,
  onDeselect,
  searchState,
}: FilterSelectionsProps) => {
  const { getKioskSpacing } = useKioskSpacing();

  const labelProps: TypographyProps = useMemo(
    () => ({
      maxLines: 2,
      overflowWrap: 'anywhere',
      variant: 'body-bold',
    }),
    []
  );

  if (filterGroup.typeahead || filterGroup.type === 'range') {
    return null;
  }
  const { items, key, modalItemsPerRow } = filterGroup;

  return (
    <Flex flexDirection="column">
      <Flex justifyContent="space-between">
        <Typography mb={16} variant="header-bold">
          {filterGroup.label}
        </Typography>
        <Link
          onClick={() => onDeselect(filterGroup.key as ProductFilterKeys)}
          ariaLabel={`${filterGroup.label} - deselect all`}
        >
          <Typography>Deselect all</Typography>
        </Link>
      </Flex>
      <Grid
        gap={16}
        gridTemplateColumns={`repeat(${modalItemsPerRow ?? 4}, 1fr)`}
      >
        {items.map((item) => {
          const specificKey: ProductFilterKeys = (
            key === ('deals_and_rewards' as FilterKeys) && item.key
              ? item.key
              : key
          ) as ProductFilterKeys;

          return (
            <FilterButton
              alignment="left"
              full
              disabled={!item.count}
              key={item.label}
              aria-label={`${filterGroup.label} -  ${item.label}`}
              label={
                <Flex alignItems="center" flexDirection="row">
                  {/* It is necessary to render the icon here instead of using the start icon property of `Button` in order
                  for text wrapping to work */}
                  {item.icon}
                  {item.icon ? (
                    <Typography
                      maxWidth="75%"
                      {...labelProps}
                      ml={getKioskSpacing(16, 8)}
                    >
                      {item.label}
                    </Typography>
                  ) : (
                    <Typography {...labelProps}>{item.label}</Typography>
                  )}
                </Flex>
              }
              onClick={() => onChange(specificKey, item.value.toString())}
              variant={
                isFilterSelected(searchState, specificKey, item)
                  ? 'tertiary-selected'
                  : 'tertiary'
              }
            />
          );
        })}
      </Grid>
    </Flex>
  );
};
